import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  NumberField,
  DateField,
  FunctionField,
  GET_ONE,
  Pagination,
  ReferenceManyField,
  showNotification,
  TextField,
  translate as translateHOC,
  withDataProvider,
} from 'react-admin';
import { Button, Grid, Typography, withStyles } from '@material-ui/core';
import InsertDriveFile from '@material-ui/icons/InsertDriveFile';
import withGrants from '../../WithGrants';
import Header from '../../../shared/header';
import CustomDatagrid from '../../../shared/custom-datagrid';
import InvoiceFilters from '../../invoice/list/invoiceFilters';
import Refund from '../actions/Refund';
import StatusChip from '../../../shared/status-chip';
import { statusInfos } from '../../../core/data-fields/data-invoices';
import { getAlternateStatus, getInvoiceDisplayedActions } from '../../../helpers/invoiceHelpers';
import SetAsPaid from '../actions/SetAsPaid';
import { useUnitInvoiceActions } from '../useUnitInvoiceActions';
import { calculateTimeDifferenceInDays } from '../../../shared/utils/date';

const styles = ({ spacing: { unit } }) => ({
  actions: {
    gap: `${unit}px`,
    padding: `${unit}px 0`,
  },
  actionButton: {
    height: 'fit-content',
  },
  icon: {
    marginRight: 8,
    fontSize: 20,
  },
});

const Actions = withStyles(styles)(({ classes, translate, invoice, actions }) => {
  return (
    <Grid container spacing={8} className={classes.actions}>
      {actions.map(({ action, label, callback, icon }) => {
        if (
          !getInvoiceDisplayedActions({
            action,
            invoiceId: invoice.id,
            invoiceStatus: getAlternateStatus({
              isPaidExternally: invoice.isPaidExternally,
              mainStatus: invoice.invoicesStatus,
              paymentInProgress: invoice.paymentInProgress,
              refundInProgress: invoice.refundInProgress,
            }),
            paymentInProgress: invoice.paymentInProgress,
            refundInProgress: invoice.refundInProgress,
          })
        ) {
          return null;
        }

        return (
          <Button
            key={action}
            variant="outlined"
            color="primary"
            onClick={() => callback(invoice.id, invoice)}
            className={classes.actionButton}
          >
            {icon}
            {translate(label)}
          </Button>
        );
      })}
    </Grid>
  );
});

const MAX_PERIOD_IN_DAYS = 366;

const UnitInvoicesList = props => {
  const { dataProvider, translate, record, dispatch, classes, timezone } = props;

  const defaultFilter = useMemo(() => {
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);
    return {
      startDate: oneYearAgo.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      timezone,
    };
  }, [timezone]);

  const [currentSelection, setCurrentSelection] = useState();
  const [filterValues, setFiltersValues] = useState(defaultFilter);

  // Dialogs states
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState({});
  const [refundableReceipts, setRefundableReceipts] = useState([]);
  const [refundedReceipts, setRefundedReceipts] = useState([]);
  const [setAsPaidDialogOpen, setSetAsPaidDialogOpen] = useState(false);

  const pagination = useRef();

  const role = localStorage.getItem('role');
  const isAdmin = role === 'admin';

  const getDataGridProps = useCallback(() => {
    const datagridProps = { ...props };
    ['classes', 'translate', 'downloadPdf', 'dataProvider', 'dispatch'].forEach(propName => {
      delete datagridProps[propName];
    });

    return datagridProps;
  }, [props]);

  const setFilters = (key, value) => setFiltersValues(prev => ({ ...prev, [key]: value }));

  const resetDates = () => setFiltersValues(defaultFilter);

  let defaultColumns = ['id', 'creationDate', 'amount', 'type', 'status', 'actions'];

  if (!isAdmin) {
    defaultColumns = defaultColumns.filter(column => column !== 'actions');
  }

  const openRefundDialog = useCallback(async (invoiceId, invoice) => {
    try {
      const { data: refundableData } = await dataProvider(GET_ONE, 'businessUnitInvoiceRefundableAmount', {
        field: 'refundableAmount',
        id: invoiceId,
      });
      setSelectedInvoice(invoice);
      setRefundableReceipts((refundableData && refundableData.refundablePaymentReceipts) || []);
      setRefundedReceipts((refundableData && refundableData.refundedPaymentReceipts) || []);
      setRefundDialogOpen(true);
    } catch (error) {
      console.error(`Error while fetching refundable amount for invoice ${invoiceId}`, error);
      dispatch(
        showNotification('notification.invoices.refundFailure', 'info', { messageArgs: { error_message: error } })
      );
    }
  }, []);

  const openSetAsPaidDialog = (_, invoice) => {
    setSelectedInvoice(invoice);
    setSetAsPaidDialogOpen(true);
  };
  const closeSetAsPaidDialog = () => {
    setSetAsPaidDialogOpen(false);
  };

  const { cancel, downloadInvoicePdf, downloadRefundNote, refund, retryPayment, setAsPaid } = useUnitInvoiceActions({
    dataProvider,
    selectedInvoiceId: selectedInvoice.id,
    dispatch,
  });

  const timeDifferenceInDays = calculateTimeDifferenceInDays(
    new Date(filterValues.startDate),
    new Date(filterValues.endDate)
  );

  return (
    <>
      <InvoiceFilters
        translate={translate}
        filterValues={filterValues}
        setFilters={setFilters}
        resetDates={resetDates}
        isFilteredDatesOverLimit={timeDifferenceInDays > MAX_PERIOD_IN_DAYS}
        periodLimit={12}
      />
      <Header label="menu.unitInvoices" />
      {timeDifferenceInDays > MAX_PERIOD_IN_DAYS ? (
        <Typography variant="body2">{translate('resources.invoice.messages.invalidFilters')}</Typography>
      ) : (
        <ReferenceManyField
          {...props}
          pagination={<Pagination ref={pagination.current} rowsPerPageOptions={[10, 25, 50]} />}
          record={record}
          filter={filterValues}
          reference="businessUnitInvoices"
          target="id"
          currentSelection={currentSelection}
        >
          <CustomDatagrid
            {...getDataGridProps()}
            onColumnChange={selection => {
              setCurrentSelection(selection);
            }}
            defaultColumns={defaultColumns}
            id="Selenium-Business-Invoices-Unit-Table"
          >
            <TextField key="id" source="id" sortable={false} />
            <TextField key="pspReference" source="pspReference" sortable={false} />
            <DateField key="creationDate" source="invoiceDate" sortable={false} />
            <NumberField key="amount" source="amount" options={{ style: 'currency' }} sortable={false} />
            <FunctionField
              key="type"
              source="type"
              sortable={false}
              render={() => translate('resources.business.fields.subscription')}
            />
            <FunctionField
              key="status"
              source="invoicesStatus"
              sortable={false}
              render={ref => {
                if (ref && ref.invoicesStatus) {
                  return ref.paidExternally || (ref.pspName && ref.pspName === 'ext_wire_transfer') ? (
                    <StatusChip
                      label={translate(`resources.invoice.status.PAID_EXTERNALLY`)}
                      {...statusInfos[ref.invoicesStatus]}
                    />
                  ) : (
                    <StatusChip
                      label={translate(`resources.invoice.status.${ref.invoicesStatus}`)}
                      {...statusInfos[ref.invoicesStatus]}
                    />
                  );
                }
                return null;
              }}
            />
            <FunctionField
              key="actions"
              label="resources.invoice.fields.actions"
              sortable={false}
              render={invoice => (
                <Actions
                  translate={translate}
                  invoice={invoice}
                  actions={[
                    {
                      action: 'invoice',
                      label: 'resources.invoice.downloadButton',
                      callback: () => downloadInvoicePdf(invoice.id),
                      icon: <InsertDriveFile className={classes.icon} />,
                    },
                    { action: 'refund', label: 'resources.invoice.refundInfo', callback: openRefundDialog },
                    {
                      action: 'refundNote',
                      label: 'resources.invoice.refundNote',
                      callback: () => downloadRefundNote(undefined, invoice.id),
                      icon: <InsertDriveFile className={classes.icon} />,
                    },
                    { action: 'pay', label: 'resources.invoice.retryPayment', callback: retryPayment },
                    {
                      action: 'cancelInvoice',
                      label: 'resources.invoice.cancel',
                      callback: cancel,
                    },
                    {
                      action: 'externalPayment',
                      label: 'resources.invoice.setAsPaid',
                      callback: openSetAsPaidDialog,
                    },
                  ]}
                />
              )}
            />
          </CustomDatagrid>
        </ReferenceManyField>
      )}
      <Refund
        onClose={() => setRefundDialogOpen(false)}
        open={refundDialogOpen}
        refund={payload =>
          refund(selectedInvoice.id, payload, selectedInvoice, () =>
            setSelectedInvoice({
              ...selectedInvoice,
              refundInProgress: true,
            })
          )
        }
        refundableReceipts={refundableReceipts}
        refundedReceipts={refundedReceipts}
        invoiceId={selectedInvoice.id}
        downloadRefundNote={downloadRefundNote}
        inProgress={selectedInvoice.refundInProgress}
      />
      <SetAsPaid
        open={setAsPaidDialogOpen}
        onClose={closeSetAsPaidDialog}
        invoiceId={selectedInvoice.id}
        setAsPaid={payload => setAsPaid(selectedInvoice.id, payload, selectedInvoice)}
      />
    </>
  );
};

const mapStateToProps = state => ({
  timezone: state.configuration.fleet.timezone,
});

UnitInvoicesList.propTypes = {
  classes: PropTypes.shape({}),
  dataProvider: PropTypes.func,
  dispatch: PropTypes.func,
  record: PropTypes.shape({}),
  timezone: PropTypes.string,
  translate: PropTypes.func,
};

export default compose(
  withGrants,
  withDataProvider,
  translateHOC,
  connect(mapStateToProps),
  withStyles(styles)
)(UnitInvoicesList);
